import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Spinner, Modal, Alert } from 'reactstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import { getPresignedUrl, uploadDocument, validateFiles } from '../helpers/fileUpload';
import {
  createMeter,
  updateMeter,
} from '../store/meter/actions';

// Measurement units grouped by category
const getMeasurementUnitGroups = (t) => [
  {
    label: t('meters.unit_distance', 'DISTANCE'),
    options: [
      { value: 'Miles', label: t('meters.unit_miles', 'Miles') },
      { value: 'Feet', label: t('meters.unit_feet', 'Feet') },
      { value: 'Inches', label: t('meters.unit_inches', 'Inches') },
      { value: 'Kilometers', label: t('meters.unit_kilometers', 'Kilometers') },
      { value: 'Meters', label: t('meters.unit_meters', 'Meters') },
      { value: 'Centimeters', label: t('meters.unit_centimeters', 'Centimeters') },
      { value: 'Millimeters', label: t('meters.unit_millimeters', 'Millimeters') },
    ],
  },
  {
    label: t('meters.unit_volume', 'VOLUME'),
    options: [
      { value: 'Gallons', label: t('meters.unit_gallons', 'Gallons') },
      { value: 'Liters', label: t('meters.unit_liters', 'Liters') },
      { value: 'Milliliters', label: t('meters.unit_milliliters', 'Milliliters') },
      { value: 'Cubic Meters', label: t('meters.unit_cubic_meters', 'Cubic Meters') },
    ],
  },
  {
    label: t('meters.unit_temperature', 'TEMPERATURE'),
    options: [
      { value: 'Fahrenheit', label: t('meters.unit_fahrenheit', 'Fahrenheit') },
      { value: 'Celsius', label: t('meters.unit_celsius', 'Celsius') },
      { value: 'Kelvin', label: t('meters.unit_kelvin', 'Kelvin') },
    ],
  },
  {
    label: t('meters.unit_velocity', 'VELOCITY'),
    options: [
      { value: 'mph', label: t('meters.unit_mph', 'mph') },
      { value: 'km/h', label: t('meters.unit_kmh', 'km/h') },
      { value: 'm/s', label: t('meters.unit_ms', 'm/s') },
    ],
  },
  {
    label: t('meters.unit_acceleration', 'ACCELERATION'),
    options: [
      { value: 'm/s²', label: t('meters.unit_ms2', 'm/s²') },
      { value: 'g-force', label: t('meters.unit_gforce', 'g-force') },
    ],
  },
  {
    label: t('meters.unit_electrical', 'ELECTRICAL'),
    options: [
      { value: 'Volts', label: t('meters.unit_volts', 'Volts') },
      { value: 'Amps', label: t('meters.unit_amps', 'Amps') },
      { value: 'Watts', label: t('meters.unit_watts', 'Watts') },
      { value: 'kWh', label: t('meters.unit_kwh', 'kWh') },
      { value: 'Ohms', label: t('meters.unit_ohms', 'Ohms') },
    ],
  },
  {
    label: t('meters.unit_other', 'OTHER'),
    options: [
      { value: 'Hours', label: t('meters.unit_hours', 'Hours') },
      { value: 'Cycles', label: t('meters.unit_cycles', 'Cycles') },
      { value: 'PSI', label: t('meters.unit_psi', 'PSI') },
      { value: 'Kilograms', label: t('meters.unit_kilograms', 'Kilograms') },
      { value: 'Grams', label: t('meters.unit_grams', 'Grams') },
      { value: 'Pounds', label: t('meters.unit_pounds', 'Pounds') },
    ],
  },
];

const getReadingFrequencyUnits = (t) => [
  { value: 'NONE', label: t('meters.freq_none', 'None') },
  { value: 'HOUR', label: t('meters.freq_hour', 'Hours') },
  { value: 'DAY', label: t('meters.freq_day', 'Days') },
  { value: 'WEEK', label: t('meters.freq_week', 'Weeks') },
  { value: 'MONTH', label: t('meters.freq_month', 'Months') },
  { value: 'YEAR', label: t('meters.freq_year', 'Years') },
];

const MeterForm = ({ isOpen, toggle, editData, onSave }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { saving, saveError } = useSelector((state) => state.Meter);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = user?.activeOrganizationId ?? null;

  const isEdit = !!editData;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    measurementUnit: '',
    assetId: '',
    locationId: '',
    readingFrequency: 'NONE',
    readingFrequencyInterval: 0,
    warningThreshold: '',
    alarmThreshold: '',
  });
  const [errors, setErrors] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Asset search state
  const [assets, setAssets] = useState([]);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAssetDisplay, setSelectedAssetDisplay] = useState('');

  // Locations
  const [locations, setLocations] = useState([]);

  const selectStyles = (hasError) => ({
    control: (base, state) => ({
      ...base,
      borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
      boxShadow: hasError
        ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
        : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
      '&:hover': { borderColor: '#86b7fe' }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': { backgroundColor: '#2e669a' }
    }),
    group: (base) => ({
      ...base,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    groupHeading: (base) => ({
      ...base,
      fontWeight: '700',
      fontSize: '0.7rem',
      color: '#6c757d',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }),
  });

  // Fetch locations
  useEffect(() => {
    if (organizationId) {
      api.get(`organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`)
        .then(({ data }) => setLocations(data.response?.[0]?.locations || []))
        .catch((err) => console.error('Error fetching locations:', err));
    }
  }, [organizationId]);

  // Fetch assets on search
  const fetchAssets = useCallback(async () => {
    try {
      const params = {
        organizationId,
        count: 5,
        search: assetSearchTerm,
        hierarchy: false,
      };
      const { data } = await api.get('asset/all', { params });
      setAssets(data?.response?.[0]?.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, [assetSearchTerm, organizationId]);

  useEffect(() => {
    if (assetSearchTerm.length > 0) {
      const debounce = setTimeout(fetchAssets, 300);
      return () => clearTimeout(debounce);
    }
  }, [assetSearchTerm, fetchAssets]);

  // Init form data
  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          name: editData.name || '',
          description: editData.description || '',
          measurementUnit: editData.measurementUnit || '',
          assetId: editData.assetId || '',
          locationId: editData.locationId || '',
          readingFrequency: editData.readingFrequency || 'NONE',
          readingFrequencyInterval: editData.readingFrequencyInterval || 0,
          warningThreshold: editData.warningThreshold != null ? String(editData.warningThreshold) : '',
          alarmThreshold: editData.alarmThreshold != null ? String(editData.alarmThreshold) : '',
        });
        setSelectedAssetDisplay(editData.assetName || (editData.assetId ? `Asset #${editData.assetId}` : ''));
        setUploadedImages(editData.imageUrls || []);
        setUploadedDocs(editData.documentUrls || []);
      } else {
        setFormData({
          name: '',
          description: '',
          measurementUnit: '',
          assetId: '',
          locationId: '',
          readingFrequency: 'NONE',
          readingFrequencyInterval: 0,
          warningThreshold: '',
          alarmThreshold: '',
        });
        setSelectedAssetDisplay('');
        setUploadedImages([]);
        setUploadedDocs([]);
      }
      setErrors({});
      setAssetSearchTerm('');
      setShowAssetDropdown(false);
    }
  }, [isOpen, editData]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const handleAssetSelect = (asset) => {
    const locationId = asset.currentLocation
      ? locations?.find(loc => loc.locationName === asset.currentLocation)?.id
      : null;

    setFormData((prev) => ({
      ...prev,
      assetId: asset.id,
      locationId: locationId || prev.locationId,
    }));
    setSelectedAssetDisplay(`${asset.assetName}${asset.serialNumber ? ` - ${asset.serialNumber}` : ''}`);
    setAssetSearchTerm('');
    setShowAssetDropdown(false);
  };

  const handleAssetSearchChange = (e) => {
    const value = e.target.value;
    setAssetSearchTerm(value);
    setShowAssetDropdown(value.length > 0);
    if (value.length === 0) {
      setFormData((prev) => ({ ...prev, assetId: '' }));
      setSelectedAssetDisplay('');
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validation = validateFiles(files);
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, files: validation.message }));
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        const url = isImage ? await getPresignedUrl(file) : await uploadDocument(file);
        if (url) {
          if (isImage) {
            setUploadedImages((prev) => [...prev, url]);
          } else {
            setUploadedDocs((prev) => [...prev, url]);
          }
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
      setErrors((prev) => ({ ...prev, files: t('shared.failed_upload_file', 'Failed to upload file') }));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeDoc = (index) => {
    setUploadedDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t('meters.name_required', 'Name is required');
    }
    if (!formData.measurementUnit.trim()) {
      newErrors.measurementUnit = t('meters.unit_required', 'Measurement unit is required');
    }
    if (!formData.assetId) {
      newErrors.assetId = t('shared.asset_is_required', 'Asset is required');
    }

    const warning = formData.warningThreshold !== '' ? parseFloat(formData.warningThreshold) : null;
    const alarm = formData.alarmThreshold !== '' ? parseFloat(formData.alarmThreshold) : null;

    if (warning !== null && isNaN(warning)) {
      newErrors.warningThreshold = t('meters.validation_number', 'Must be a valid number');
    }
    if (alarm !== null && isNaN(alarm)) {
      newErrors.alarmThreshold = t('meters.validation_number', 'Must be a valid number');
    }
    if (warning !== null && alarm !== null && !isNaN(warning) && !isNaN(alarm) && alarm <= warning) {
      newErrors.alarmThreshold = t('meters.threshold_validation', 'Alarm threshold must be greater than warning threshold');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;

    const payload = {
      name: formData.name,
      description: formData.description || null,
      measurementUnit: formData.measurementUnit,
      assetId: formData.assetId || null,
      locationId: formData.locationId || null,
      readingFrequency: formData.readingFrequency || 'NONE',
      readingFrequencyInterval: formData.readingFrequencyInterval || 0,
      warningThreshold: formData.warningThreshold !== '' ? parseFloat(formData.warningThreshold) : null,
      alarmThreshold: formData.alarmThreshold !== '' ? parseFloat(formData.alarmThreshold) : null,
      imageUrls: uploadedImages.length > 0 ? uploadedImages : null,
      documentUrls: uploadedDocs.length > 0 ? uploadedDocs : null,
    };

    if (isEdit) {
      dispatch(updateMeter({ ...payload, id: editData.id }, () => {
        if (onSave) onSave();
      }));
    } else {
      dispatch(createMeter(payload, () => {
        if (onSave) onSave();
      }));
    }
  }, [formData, uploadedImages, uploadedDocs, isEdit, editData, dispatch, onSave, validate]);

  // Find the selected unit option for the dropdown
  const selectedUnitOption = formData.measurementUnit
    ? { value: formData.measurementUnit, label: formData.measurementUnit }
    : null;

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header text-white">
          <h5 className="modal-title fs-4">
            {isEdit
              ? t('meters.edit_meter', 'Edit Meter')
              : t('meters.create_meter', 'New Meter')}
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={toggle}
            aria-label={t('shared.close', 'Close')}
          ></button>
        </div>

        <div className="modal-body p-4">
          {saveError && <Alert color="danger" className="mb-3">{saveError}</Alert>}

          {/* 1. Name */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="form-group">
                <label htmlFor="meterName" className="form-label fw-medium">
                  {t('meters.name', 'Name')} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="meterName"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={t('meters.name_placeholder', 'Enter Meter Name')}
                  maxLength={255}
                />
                {errors.name && <small className="text-danger">{errors.name}</small>}
              </div>
            </Col>
          </Row>

          {/* 2. Meter Type — read-only Manual indicator */}
          <Row className="mb-4">
            <Col md={12}>
              <label className="form-label fw-medium">
                {t('meters.meter_type', 'Meter Type')}
              </label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary active"
                  style={{ pointerEvents: 'none', fontWeight: 500 }}
                >
                  <FeatherIcon icon="user" size={14} className="me-1" />
                  {t('meters.type_manual', 'Manual')}
                </button>
              </div>
            </Col>
          </Row>

          {/* 3. Description */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="form-group">
                <label className="form-label fw-medium">
                  {t('meters.description', 'Description')}
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t('meters.description_placeholder', 'Add a description')}
                  maxLength={4096}
                />
              </div>
            </Col>
          </Row>

          {/* 4. Measurement Unit — grouped creatable dropdown */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="form-group">
                <label className="form-label fw-medium">
                  {t('meters.measurement_unit', 'Measurement Unit')} <span className="text-danger">*</span>
                </label>
                <CreatableSelect
                  options={getMeasurementUnitGroups(t)}
                  value={selectedUnitOption}
                  onChange={(selected) => handleChange('measurementUnit', selected?.value || '')}
                  onCreateOption={(inputValue) => handleChange('measurementUnit', inputValue)}
                  formatCreateLabel={(inputValue) => `${t('meters.use_custom_unit', 'Use')} "${inputValue}"`}
                  placeholder={t('meters.unit_placeholder', 'Start typing to search or customize...')}
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={selectStyles(!!errors.measurementUnit)}
                />
                {errors.measurementUnit && <small className="text-danger">{errors.measurementUnit}</small>}
              </div>
            </Col>
          </Row>

          {/* 5. Asset + Location — side by side */}
          <Row className="mb-4 g-3">
            <Col md={6}>
              <div className="form-group">
                <label className="form-label fw-medium">
                  {t('meters.asset', 'Asset')} <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                  {selectedAssetDisplay ? (
                    <div
                      className="form-control d-flex align-items-center justify-content-between"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedAssetDisplay('');
                        setFormData((prev) => ({ ...prev, assetId: '' }));
                      }}
                    >
                      <span className="text-truncate">
                        <FeatherIcon icon="tool" size={14} className="me-1 text-muted" />
                        {selectedAssetDisplay}
                      </span>
                      <FeatherIcon icon="x" size={16} className="text-muted flex-shrink-0" />
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t('meters.asset_placeholder', 'Start typing...')}
                        value={assetSearchTerm}
                        onChange={handleAssetSearchChange}
                        onFocus={() => {
                          if (assetSearchTerm.length > 0) setShowAssetDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowAssetDropdown(false), 200)}
                      />
                      {showAssetDropdown && assets.length > 0 && (
                        <div
                          className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                          style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto', top: '100%' }}
                        >
                          {assets.map((asset) => (
                            <div
                              key={asset.id}
                              className="p-2 border-bottom d-flex align-items-center"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleAssetSelect(asset)}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <div className="flex-grow-1">
                                <div className="fw-medium text-dark">{asset.assetName}</div>
                                <small className="text-muted">
                                  {asset.serialNumber} {asset.currentLocation ? `• ${asset.currentLocation}` : ''}
                                </small>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                        <div
                          className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                          style={{ zIndex: 1050, top: '100%' }}
                        >
                          {t('shared.no_results', 'No results found')}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {errors.assetId && <small className="text-danger">{errors.assetId}</small>}
              </div>
            </Col>
            <Col md={6}>
              <div className="form-group">
                <label className="form-label fw-medium">
                  {t('meters.location', 'Location')}
                </label>
                <Select
                  options={locations?.map((location) => ({
                    value: location.id,
                    label: location.locationName,
                  })) || []}
                  value={
                    locations?.find(loc => loc.id === formData.locationId)
                      ? { value: formData.locationId, label: locations.find(loc => loc.id === formData.locationId)?.locationName }
                      : null
                  }
                  onChange={(selected) => handleChange('locationId', selected?.value || '')}
                  isClearable
                  isSearchable
                  placeholder={t('meters.location_placeholder', 'Start typing...')}
                  classNamePrefix="react-select"
                  styles={selectStyles(false)}
                />
              </div>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* 6. Reading Frequency — "Every [N] [Unit]" */}
          <Row className="mb-4">
            <Col md={12}>
              <label className="form-label fw-medium">
                {t('meters.reading_frequency', 'Reading Frequency')}
              </label>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted fw-medium">{t('meters.every', 'Every')}</span>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  style={{ width: '80px' }}
                  value={formData.readingFrequencyInterval}
                  onChange={(e) => handleChange('readingFrequencyInterval', parseInt(e.target.value) || 0)}
                  disabled={formData.readingFrequency === 'NONE'}
                />
                <div style={{ width: '160px' }}>
                  <Select
                    options={getReadingFrequencyUnits(t)}
                    value={getReadingFrequencyUnits(t).find(r => r.value === formData.readingFrequency) || getReadingFrequencyUnits(t)[0]}
                    onChange={(selected) => {
                      const val = selected?.value || 'NONE';
                      handleChange('readingFrequency', val);
                      if (val === 'NONE') {
                        handleChange('readingFrequencyInterval', 0);
                      }
                    }}
                    isSearchable={false}
                    classNamePrefix="react-select"
                    styles={selectStyles(false)}
                  />
                </div>
              </div>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* 7. Threshold Settings */}
          <Row className="mb-4 g-3">
            <Col md={12}>
              <label className="form-label fw-medium mb-3">
                {t('meters.threshold_settings', 'Threshold Settings')}
              </label>
            </Col>
            <Col md={6}>
              <div className="form-group">
                <label className="form-label text-muted small">
                  {t('meters.warning_threshold', 'Warning')}
                </label>
                <input
                  type="number"
                  step="any"
                  className={`form-control ${errors.warningThreshold ? 'is-invalid' : ''}`}
                  value={formData.warningThreshold}
                  onChange={(e) => handleChange('warningThreshold', e.target.value)}
                  placeholder={t('meters.threshold_placeholder', 'e.g., 80')}
                />
                {errors.warningThreshold && <small className="text-danger">{errors.warningThreshold}</small>}
              </div>
            </Col>
            <Col md={6}>
              <div className="form-group">
                <label className="form-label text-muted small">
                  {t('meters.alarm_threshold', 'Alarm')}
                </label>
                <input
                  type="number"
                  step="any"
                  className={`form-control ${errors.alarmThreshold ? 'is-invalid' : ''}`}
                  value={formData.alarmThreshold}
                  onChange={(e) => handleChange('alarmThreshold', e.target.value)}
                  placeholder={t('meters.threshold_placeholder_alarm', 'e.g., 100')}
                />
                {errors.alarmThreshold && <small className="text-danger">{errors.alarmThreshold}</small>}
              </div>
            </Col>
            <Col md={12}>
              <small className="text-muted">
                {t('meters.threshold_note', 'The alarm threshold value should be higher than the warning threshold value.')}
              </small>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* 8. Additional Info — File Upload */}
          <Row className="mb-3">
            <Col md={12}>
              <label className="form-label fw-medium">
                {t('meters.additional_info', 'Additional Info')}
              </label>

              {/* Uploaded files preview */}
              {(uploadedImages.length > 0 || uploadedDocs.length > 0) && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {uploadedImages.map((url, i) => (
                    <div key={`img-${i}`} className="position-relative" style={{ width: 100, height: 80 }}>
                      <img
                        src={url}
                        alt=""
                        className="rounded border"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute"
                        style={{ top: -6, right: -6, padding: '1px 4px', fontSize: '10px', borderRadius: '50%' }}
                        onClick={() => removeImage(i)}
                      >
                        <FeatherIcon icon="x" size={10} />
                      </button>
                    </div>
                  ))}
                  {uploadedDocs.map((url, i) => (
                    <div key={`doc-${i}`} className="d-flex align-items-center border rounded p-2 position-relative">
                      <FeatherIcon icon="file" size={16} className="me-1 text-muted" />
                      <small className="text-truncate" style={{ maxWidth: 100 }}>
                        {url.split('/').pop()}
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger ms-1"
                        style={{ padding: '1px 4px', fontSize: '10px', borderRadius: '50%' }}
                        onClick={() => removeDoc(i)}
                      >
                        <FeatherIcon icon="x" size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Drop zone */}
              <div
                className="border rounded p-4 text-center"
                style={{
                  borderStyle: 'dashed',
                  borderColor: '#b8d4f0',
                  backgroundColor: '#f0f7ff',
                  cursor: 'pointer',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <Spinner size="sm" color="primary" />
                ) : (
                  <>
                    <FeatherIcon icon="camera" size={24} className="text-primary mb-1" />
                    <div className="text-primary small fw-medium">
                      {t('meters.add_pictures_files', 'Add Pictures/Files')}
                    </div>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              {errors.files && <small className="text-danger mt-1 d-block">{errors.files}</small>}
            </Col>
          </Row>
        </div>

        <div className="modal-footer bg-light border-top-0">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={toggle}
            disabled={saving || uploading}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving || uploading}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                {isEdit ? t('shared.updating', 'Updating...') : t('shared.creating', 'Creating...')}
              </>
            ) : (
              isEdit ? t('shared.save', 'Save') : t('meters.create', 'Create')
            )}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default MeterForm;
